import { useEffect } from 'react';
import { messages } from '../../i18n/translations';
import { useI18n } from '../../i18n/I18nProvider';

const flatten = (source, result = {}) => {
  Object.values(source || {}).forEach((value) => {
    if (typeof value === 'string') result[value] = value;
    else if (value && typeof value === 'object') flatten(value, result);
  });
  return result;
};

const flattenPairs = (fr, en, result = new Map()) => {
  Object.keys(fr || {}).forEach((key) => {
    const left = fr[key];
    const right = en?.[key];
    if (typeof left === 'string' && typeof right === 'string') result.set(left, right);
    else if (left && typeof left === 'object') flattenPairs(left, right, result);
  });
  return result;
};

const phraseMap = new Map([
  ['Trouver un nouveau projet', 'Find a new project'],
  ['projet compatible', 'matching project'],
  ['projets compatibles', 'matching projects'],
  ['Liste', 'List'], ['Carte', 'Map'], ['Dans mon rayon', 'Within my radius'], ['Recommandés', 'Recommended'],
  ['Ville, besoin ou client…', 'City, service or client…'], ['Distance moyenne', 'Average distance'], ['À moins de 10 km', 'Within 10 km'],
  ['Mon profil artisan', 'My professional profile'], ['Changer ma photo', 'Change my photo'],
  ['Profil validé', 'Verified profile'], ['Identité professionnelle', 'Professional identity'],
  ['Identité et contact', 'Identity and contact'], ['Adresse et localisation', 'Address and location'],
  ['Prénom', 'First name'], ['Nom', 'Last name'], ['Téléphone', 'Phone'], ['Adresse principale', 'Main address'],
  ['Votre adresse postale', 'Your postal address'], ['Zone sensible', 'Sensitive area'],
  ['Supprimer mon compte', 'Delete my account'], ['Enregistrer les modifications', 'Save changes'],
  ['Vérifier puis enregistrer', 'Review and save'], ['Modifications non enregistrées', 'Unsaved changes'],
  ['Profil enregistré', 'Profile saved'], ['Profil à jour', 'Profile up to date'],
  ['Mes demandes', 'My requests'], ['Demander un devis', 'Request a quote'], ['Contrat reçu', 'Contract received'],
  ['Historique', 'History'], ['Demandes', 'Requests'], ['Profil', 'Profile'],
  ['Menuiserie', 'Joinery and windows'], ['Peinture', 'Painting'], ['Plomberie', 'Plumbing'], ['Électricité', 'Electrical work'],
  ['1 photo(s)', '1 photo'], ['photo(s)', 'photos'], ['Profil & avis', 'Profile & reviews'],
  ['JPG, PNG ou WebP · 8 Mo maximum. La photo est stockée dans votre espace sécurisé.', 'JPG, PNG or WebP · 8 MB maximum. Your photo is stored securely.'],
  ['Explorer', 'Explore'], ['Mon planning', 'My planner'], ['Mes missions', 'My jobs'], ['Ma vitrine', 'My portfolio'],
  ['Mon profil', 'My profile'], ['Déconnexion', 'Log out'], ['Nouveau devis', 'New quote'],
  ['Action requise', 'Action needed'], ['À traiter', 'Action needed'], ['En cours', 'In progress'], ['Terminées', 'Completed'], ['Terminés', 'Completed'],
  ['Aucune action nécessaire.', 'No action needed.'], ['Enregistrement en cours…', 'Saving…'], ['Mise à jour…', 'Updating…'],
  ['Annuler', 'Cancel'], ['Fermer', 'Close'], ['Envoyer', 'Send'], ['Enregistrer', 'Save'], ['Modifier', 'Edit'], ['Supprimer', 'Delete'],
]);

const exactMap = flattenPairs(messages.fr, messages.en);
phraseMap.forEach((value, key) => exactMap.set(key, value));
const knownFrench = flatten(messages.fr);

const excludedSelector = [
  'script', 'style', 'code', 'pre', 'textarea', '[contenteditable="true"]', '[data-no-auto-translate="true"]',
  '.project-message__body', '.message-body', '.review-comment', '.user-content', '.project-description',
].join(',');

const translateText = (text) => {
  const leading = text.match(/^\s*/)?.[0] || '';
  const trailing = text.match(/\s*$/)?.[0] || '';
  const core = text.trim();
  if (!core || core.length > 180) return text;
  if (exactMap.has(core)) return `${leading}${exactMap.get(core)}${trailing}`;

  let translated = core;
  const replacements = [
    [/^(\d+) projet compatible$/i, '$1 matching project'],
    [/^(\d+) projets compatibles$/i, '$1 matching projects'],
    [/^Dans mon rayon \(([^)]+)\)$/i, 'Within my radius ($1)'],
    [/^(\d+) photo\(s\)$/i, '$1 photos'],
    [/^Dernière sauvegarde à (.+)$/i, 'Last saved at $1'],
    [/^À corriger : (.+)$/i, 'Please review: $1'],
  ];
  replacements.some(([pattern, replacement]) => {
    if (!pattern.test(core)) return false;
    translated = core.replace(pattern, replacement);
    return true;
  });
  return translated === core ? text : `${leading}${translated}${trailing}`;
};

const textStates = new WeakMap();
const attributeStates = new WeakMap();

const shouldSkip = (node) => {
  const parent = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  return !parent || parent.closest(excludedSelector);
};

/**
 * Translate only the value that is currently owned by React/the page.
 *
 * The previous bridge remembered the very first text it ever saw and, in
 * French mode, continuously wrote that old value back after every React
 * update. Dynamic values such as quote totals, counters and workflow labels
 * were therefore visually frozen at their initial value (often 0,00 €).
 *
 * We now remember a pair only when this bridge itself actually translated a
 * value. We restore it only if the DOM still contains that exact translation.
 * If React has changed the node in the meantime, the React value wins.
 */
const processTextNode = (node, language) => {
  if (shouldSkip(node)) return;

  const current = node.nodeValue || '';
  const state = textStates.get(node);

  if (language === 'fr') {
    if (state && current === state.translated) {
      node.nodeValue = state.original;
    }
    textStates.delete(node);
    return;
  }

  // Ignore the mutation generated by our own previous translation.
  if (state && current === state.translated) return;

  const translated = translateText(current);
  if (translated === current) {
    // React may have replaced a previously translated value with a new
    // dynamic value. Forget the old bridge state and leave React untouched.
    if (state) textStates.delete(node);
    return;
  }

  textStates.set(node, { original: current, translated });
  node.nodeValue = translated;
};

const processElement = (element, language) => {
  if (!(element instanceof Element) || shouldSkip(element)) return;

  const attributes = ['placeholder', 'title', 'aria-label'];
  let stored = attributeStates.get(element);
  if (!stored) {
    stored = {};
    attributeStates.set(element, stored);
  }

  attributes.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;

    const current = element.getAttribute(attribute) || '';
    const state = stored[attribute];

    if (language === 'fr') {
      if (state && current === state.translated) {
        element.setAttribute(attribute, state.original);
      }
      delete stored[attribute];
      return;
    }

    if (state && current === state.translated) return;

    const translated = translateText(current);
    if (translated === current) {
      if (state) delete stored[attribute];
      return;
    }

    stored[attribute] = { original: current, translated };
    element.setAttribute(attribute, translated);
  });
};

const walk = (root, language) => {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) processTextNode(root, language);
  if (root.nodeType === Node.ELEMENT_NODE) processElement(root, language);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let current = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) processTextNode(current, language);
    else processElement(current, language);
    current = walker.nextNode();
  }
};

const LegacyUiTranslationBridge = () => {
  const { language } = useI18n();

  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) return undefined;

    // French is the application's native/default language. Restore only text
    // that this bridge previously translated and then get completely out of
    // React's way. In particular, do NOT observe characterData in French.
    if (language === 'fr') {
      walk(document.body, 'fr');
      return undefined;
    }

    let queued = false;
    let frame = 0;
    const observer = new MutationObserver(() => queue());

    const observe = () => observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label'],
    });

    const apply = () => {
      queued = false;
      observer.disconnect();
      walk(document.body, 'en');
      observe();
    };

    function queue() {
      if (queued) return;
      queued = true;
      frame = window.requestAnimationFrame(apply);
    }

    queue();
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [language]);

  return null;
};

export default LegacyUiTranslationBridge;
